import React, { useEffect } from 'react';

import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Button, Card, Typography } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import {
    ReactHookFormInputDatePicker2,
    ReactHookFormSelect2,
    ReactHookFormSlider,
} from 'common/components/form/bootstrap';
import { timeZones } from 'common/components/segment/constants';

import * as actions from '../../actions';
import * as selectors from '../../selectors';

const ShaderSettingsForm = ({
    defaultValues,
    isMobile,
    onCloseDrawer,
    save,
    simulationStatus,
}) => {
    const { t } = useTranslation();
    const { isPlaying } = simulationStatus;

    const { control, handleSubmit, reset, getValues, watch } = useForm({
        defaultValues,
    });

    const hour = watch('hour');

    useEffect(() => reset(defaultValues), [defaultValues]);

    const handleOnSave = (values) => {
        save({ ...values, isPlaying: !isPlaying });

        if (isMobile && !isPlaying) {
            onCloseDrawer();
        }
    };

    const handleOnSliderChange = () => {
        const values = getValues();
        save({ ...values, isPlaying: false });
    };

    const handleOnChangeValues = () => {
        const values = getValues();
        save(values);
    };

    return (
        <>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 2 }}>
                <SettingsIcon color="primary" />
                <Typography fontWeight="bold" variant="body2">
                    {t('Shadow simulator')}
                </Typography>
            </Box>

            <Card>
                <Card.Body>
                    <form onSubmit={handleSubmit(handleOnSave)}>
                        <ReactHookFormInputDatePicker2
                            control={control}
                            disabled={isPlaying}
                            label={t('Date')}
                            name="date"
                            onChange={handleOnChangeValues}
                        />

                        <ReactHookFormSelect2
                            control={control}
                            disabled={isPlaying}
                            isClearable={false}
                            name="timezone"
                            label={t('Timezone')}
                            onChange={handleOnChangeValues}
                            options={[
                                {
                                    label: '',
                                    options: timeZones.map((timezone) => ({
                                        label: `(UTC${
                                            timezone.value >= 0
                                                ? `+${timezone.value}`
                                                : timezone.value
                                        } ) ${timezone.label}`,
                                        value: `${timezone.value}`,
                                    })),
                                },
                            ]}
                        />

                        <Typography sx={{ textAlign: 'right' }}>
                            <small>{hour}:00 hrs</small>
                        </Typography>

                        <ReactHookFormSlider
                            control={control}
                            disabled={isPlaying}
                            name="hour"
                            min={5}
                            max={21}
                            marks
                            onChange={handleOnSliderChange}
                            size="small"
                        />

                        <Button
                            fullWidth
                            sx={{ '&.MuiButton-root': { mb: 0 } }}
                            type="submit"
                        >
                            {t('Simulation')}{' '}
                            {!isPlaying ? (
                                <PlayCircleOutlineIcon />
                            ) : (
                                <PauseCircleOutlineIcon />
                            )}
                        </Button>
                    </form>
                </Card.Body>
            </Card>
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    defaultValues: selectors.getShaderSetttingsInitialValues,
    simulationStatus: selectors.getSimulationStatus,
});

const mapDispatchToProps = (dispatch) => ({
    save: (config) => dispatch(actions.saveShaderSettings(config)),
});

ShaderSettingsForm.propTypes = {
    defaultValues: PropTypes.object,
    isMobile: PropTypes.bool,
    onCloseDrawer: PropTypes.func,
    save: PropTypes.func,
    simulationStatus: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(ShaderSettingsForm);
